const useRelativeApiUrl = process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV_NAME === 'local'

const baseURL = useRelativeApiUrl ? '' : process.env.REACT_APP_API_URL ?? ''

type HttpResponse<T = unknown> = {
  data: T
}

type HttpError = Error & {
  response?: {
    data: unknown
  }
}

const combineURLs = (base: string, path: string) => {
  if (!path) return base
  return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
}

const buildUrl = (path: string) => {
  if (path.startsWith('http')) return path
  return combineURLs(baseURL, path)
}

const parseResponse = async <T>(response: Response): Promise<HttpResponse<T>> => {
  let data: unknown = null
  const text = await response.text()

  if (text) {
    const contentType = response.headers.get('content-type')
    const looksLikeJson = text.trim().startsWith('{') || text.trim().startsWith('[')

    if (contentType?.includes('application/json') || looksLikeJson) {
      try {
        data = JSON.parse(text)
      } catch {
        data = text
      }
    } else {
      data = text
    }
  }

  if (!response.ok) {
    const error = new Error('Request failed') as HttpError
    error.response = { data }
    throw error
  }

  return { data: data as T }
}

const request = <T>(method: string, path: string, body?: unknown): Promise<HttpResponse<T>> => {
  const options: RequestInit = { method }

  if (body !== undefined) {
    options.headers = { 'Content-Type': 'application/json' }
    options.body = JSON.stringify(body)
  }

  return fetch(buildUrl(path), options).then((response) => parseResponse<T>(response))
}

const http = {
  get: <T = unknown>(path: string) => request<T>('GET', path),
  post: <T = unknown>(path: string, body?: unknown) => request<T>('POST', path, body)
}

export default http
